import { useApolloClient } from '@apollo/client';
import type { DeleteOversightAssessmentsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import { evictField } from '@/utils/graphqlUtils';

const mapTrpcResponseToGraphQL = (trpcData: {
  affected_rows: number;
}): DeleteOversightAssessmentsMutation => {
  return {
    delete_oversight_assessment: {
      __typename: 'oversight_assessment_mutation_response',
      affected_rows: trpcData.affected_rows,
    },
  };
};

export const useDeleteOversightAssessmentsTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const apolloClient = useApolloClient();
  const mutation = useMutation({
    ...trpc.frontend.oversight.delete.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.oversight.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.oversight.oversightAssessmentCount.queryKey(),
        });
        // `getOversightAssessments` (the register / selector / linked-item
        // read) is NOT migrated — still Apollo `useQuery` on a cache-first
        // policy. Mirror the Apollo twin's `update` callback so the register
        // doesn't keep rendering the deleted rows.
        evictField(apolloClient.cache, 'oversight_assessment');
        evictField(apolloClient.cache, 'oversight_assessment_aggregate');
      },
    }),
  });

  return {
    deleteOversightAssessments: async (
      ids: string[]
    ): Promise<DeleteOversightAssessmentsMutation> => {
      const result = await mutation.mutateAsync({ Ids: ids });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
